/**
 * ContactCardEntry — client view of a "Share a contact" Client Hub entry (Matt, 2026-10-15).
 *
 * The team shares a PERSON, or an ORGANIZATION with its main contact, and says why (data.intro).
 * The client sees the details with tap-to-call / tap-to-email links plus a "Save to contacts"
 * download of a vCard built by the same helper the subscriber email attaches (api/_vcard.js). An
 * organization share downloads both cards (the company and its main contact) in one file.
 *
 * entry.data: { kind: 'person'|'org', intro,
 *               contact: { name, firstName, lastName, company, title, email, phone,
 *                          street, city, state, zip, website },
 *               org: { name, phone, email, website, street, city, state, zip } }
 * Everything is a SNAPSHOT taken when the entry was authored, so the public hub never reads the
 * Contacts table. Entries made before organizations existed have no kind and read as a person.
 */

import { J } from '../journeyUI.jsx'
import Markdown from '../../components/Markdown.jsx'
import { buildShareVCard, vcardFilename, contactDisplayName, isOrgShare, shareDisplayName } from '../../../../api/_vcard.js'

const row = { display: 'flex', gap: 14, padding: '9px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)', alignItems: 'baseline' }
const key = { ...J.eyebrow, marginBottom: 0, width: 74, flexShrink: 0, letterSpacing: '0.24em' }
const val = { ...J.body, fontSize: 14, lineHeight: 1.5, color: '#07272D', wordBreak: 'break-word' }
const link = { color: '#07272D', textDecoration: 'none', borderBottom: '0.5px solid rgba(7,39,45,0.25)' }

function saveCard(d) {
  const blob = new Blob([buildShareVCard(d)], { type: 'text/vcard;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = vcardFilename(shareDisplayName(d))
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/* Phone / email / address / website lines for a person or an organization. */
function Lines({ x }) {
  if (!x) return null
  const cityLine = [x.city, [x.state, x.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
  const address = [x.street, cityLine].filter(Boolean)
  const site = x.website ? (/^https?:\/\//i.test(x.website) ? x.website : 'https://' + x.website) : ''
  return (
    <>
      {x.phone && (
        <div style={row}><span style={key}>Phone</span><a href={'tel:' + x.phone.replace(/[^\d+]/g, '')} style={{ ...val, ...link }}>{x.phone}</a></div>
      )}
      {x.email && (
        <div style={row}><span style={key}>Email</span><a href={'mailto:' + x.email} style={{ ...val, ...link }}>{x.email}</a></div>
      )}
      {address.length > 0 && (
        <div style={row}><span style={key}>Address</span><span style={val}>{address.map((l, i) => <div key={i}>{l}</div>)}</span></div>
      )}
      {site && (
        <div style={row}><span style={key}>Website</span><a href={site} target="_blank" rel="noopener noreferrer" style={{ ...val, ...link }}>{x.website}</a></div>
      )}
    </>
  )
}

export default function ContactCardEntry({ entry }) {
  const d = entry.data || {}
  const org = isOrgShare(d)
  const c = d.contact || null
  const name = shareDisplayName(d)
  const mainName = c ? contactDisplayName(c) : ''

  return (
    <div>
      <div style={{ textAlign: 'center', paddingTop: 10 }}>
        <div style={{ ...J.eyebrow, marginBottom: 18 }}>Contact</div>
        <h2 style={{ ...J.title, maxWidth: 440, margin: '0 auto' }}>{entry.title || name || 'Contact'}</h2>
        <div style={{ width: 36, height: 1, background: 'rgba(7,39,45,0.24)', margin: '26px auto 30px' }} />
      </div>
      {d.intro && (
        <div style={{ ...J.body, maxWidth: 480, margin: '0 auto 26px' }}>
          <Markdown text={d.intro} />
        </div>
      )}
      {name ? (
        <div style={{ maxWidth: 420, margin: '0 auto' }}>
          {org ? (
            <>
              {entry.title && entry.title !== name && <div style={{ ...val, fontSize: 17, textAlign: 'center', marginBottom: 16 }}>{name}</div>}
              <Lines x={d.org} />
              {mainName && (
                <>
                  <div style={{ ...J.eyebrow, margin: '26px 0 6px' }}>Main contact</div>
                  <div style={{ ...val, fontSize: 16 }}>{mainName}</div>
                  {c.title && <div style={{ ...J.mono, marginTop: 2, marginBottom: 6 }}>{c.title}</div>}
                  <Lines x={{ phone: c.phone, email: c.email }} />
                </>
              )}
            </>
          ) : (
            <>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>
                {entry.title && entry.title !== name && <div style={{ ...val, fontSize: 17 }}>{name}</div>}
                {(c.title || c.company) && <div style={{ ...J.mono, marginTop: 4 }}>{[c.title, c.company].filter(Boolean).join(' · ')}</div>}
              </div>
              <Lines x={c} />
            </>
          )}
          <div style={{ textAlign: 'center', paddingTop: 22 }}>
            <button onClick={() => saveCard(d)} style={J.textLink}>{org && mainName ? 'Save both to contacts' : 'Save to contacts'}</button>
          </div>
        </div>
      ) : (
        <div style={{ ...J.body, fontStyle: 'italic', color: '#8ba6b1', textAlign: 'center', padding: '18px 0 8px' }}>
          This contact isn't available yet.
        </div>
      )}
    </div>
  )
}
